import { toast } from "react-toastify";
import { Address, User } from "@core/types";
import { getCustomerFullAddress } from "../utils";
import { useConfig } from "../providers/config";
import { useCustomerAddressesStorageData } from "../providers/customerAddressesStorage";
import { filterOutSimilarUnAuthAddresses } from "../providers/customerAddressesStorage/utils";
import { useCreateCustomerAddress, useGetCustomerAddressesLazy } from "../providers/CustomerAddresses";
import { useAuth } from "../contexts/auth-context";
import { cartDataStore } from "../providers/cartDataStore";
import { changeCartData } from "../providers/cartDataStore/useCartData";
import { useUpdateCart } from "../providers/cart";
import { useActiveOrMainShippingZone } from "../providers/shippingZones";
import { useCallback } from "react";

export const useHandleCustomerLogin = () => {
  const {
    addAddress,
    currentShippingAddressId,
    unAuthedCustomerAddresses,
    setCurrentShippingAddressId,
    resetUnAuthedCustomerAddresses,
  } = useCustomerAddressesStorageData();

  const config = useConfig();
  const { login } = useAuth();
  const { mutateAsync: updateCartAsync } = useUpdateCart();

  const { activeShippingZone } = useActiveOrMainShippingZone();
  const { mutateAsync: createCustomerAddress } = useCreateCustomerAddress();
  const { lazyQuery: getCustomerAddresses } = useGetCustomerAddressesLazy();

  const handleCustomerLogin = useCallback(
    async (loginData: User) => {
      login(loginData);

      if (config.isCustomerAdressesEnabled) {
        const customerAddresses = await getCustomerAddresses(loginData.id);

        const filteredSimilarAuthAndUnauthAddresses = filterOutSimilarUnAuthAddresses(
          customerAddresses ?? [],
          unAuthedCustomerAddresses
        );

        const newAddresses: Address[] = [];
        const failedAddressesSaved = [];

        for (const addr of filteredSimilarAuthAndUnauthAddresses) {
          const { id, ...rest } = addr;

          try {
            const response = await createCustomerAddress({ customerAddressInput: rest, customerId: loginData?.id });
            if (response) {
              newAddresses.push(response);
            }
          } catch (e: any) {
            // Postgres 23505 UNIQUE VIOLATION error
            if (e.code !== "23505") {
              failedAddressesSaved.push(addr);
            }
          }
        }

        const currentUnAuthAddress = unAuthedCustomerAddresses.find(
          (unAuthAddresses) => unAuthAddresses.id === currentShippingAddressId
        );

        console.log({ currentShippingAddressId, unAuthedCustomerAddresses });

        let matchingNewAddressForCurrentShipping: Address | undefined = undefined;

        const fullAddressOfUnAuthedCustomer = currentUnAuthAddress
          ? getCustomerFullAddress({
              city: currentUnAuthAddress?.city,
              house: currentUnAuthAddress?.house,
              street: currentUnAuthAddress?.street,
              isFlat: !!currentUnAuthAddress?.apartmentNumber,
              apartmentNumber: currentUnAuthAddress?.apartmentNumber,
            })
              ?.trim()
              ?.toLowerCase()
          : null;

        if (fullAddressOfUnAuthedCustomer) {
          if (!!newAddresses?.length) {
            matchingNewAddressForCurrentShipping = newAddresses.find((newAddress) => {
              const normalizedNewAddress = newAddress.fullAddress?.trim()?.toLowerCase();
              return normalizedNewAddress === fullAddressOfUnAuthedCustomer;
            });
          } else {
            matchingNewAddressForCurrentShipping = customerAddresses?.find((unAuthedCustomerAddress) => {
              const normalizedAddress = unAuthedCustomerAddress.fullAddress?.trim()?.toLowerCase();
              return normalizedAddress === fullAddressOfUnAuthedCustomer;
            });
          }
        }

        resetUnAuthedCustomerAddresses();

        failedAddressesSaved.forEach((failedAddressSaved) => addAddress(failedAddressSaved));

        if (matchingNewAddressForCurrentShipping?.id) {
          setCurrentShippingAddressId(matchingNewAddressForCurrentShipping.id, "useHandleCustomerLogin");
        } else if (customerAddresses?.[0]?.id && unAuthedCustomerAddresses.length > 0) {
          setCurrentShippingAddressId(customerAddresses[0].id, "useHandleCustomerLogin");
          toast.warning(
            "Обратите внимание! Адрес созданный вами не найден в базе данных и был установлен одним из существующих"
          );
        }
      }

      const cartData = cartDataStore.getState();

      if (config.isCartSyncEnabled) {
        updateCartAsync({ cart: cartData, shippingZone: activeShippingZone?.id }).then((updatedCart) => {
          changeCartData(updatedCart);
        });
      }
    },
    [
      login,
      addAddress,
      updateCartAsync,
      getCustomerAddresses,
      createCustomerAddress,
      activeShippingZone?.id,
      currentShippingAddressId,
      config.isCartSyncEnabled,
      unAuthedCustomerAddresses,
      setCurrentShippingAddressId,
      resetUnAuthedCustomerAddresses,
      config.isCustomerAdressesEnabled,
    ]
  );

  return handleCustomerLogin;
};
